import React, { useEffect, useState } from 'react';
import './About.css';

const About = () => {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="about">
      {/* Navigation */}
      <nav className="navbar">
        <div className="nav-container">
          <div className="nav-logo">
            <a href="/" className="logo-link">
              <span className="logo-text">I-Revive</span>
            </a>
          </div>
          <div className="nav-menu">
            <a href="/" className="nav-link">Home</a>
            <a href="/about" className="nav-link active">About</a>
            <a href="/contact" className="nav-link">Contact</a>
          </div>
          <div className="nav-cta">
            <a href="#preorder" className="btn btn-primary">Pre-Order</a>
          </div>
        </div>
      </nav>

      {/* Hero Section - Updated */}
      <section className="about-hero">
        <div className="container">
          <div className="hero-content-centered">
            <h1 className="hero-title-centered fade-in-up">
              Revolutionizing <span className="highlight">Foot Health</span>
            </h1>
            <p className="hero-subtitle-centered fade-in-up">
              At <span className="company-highlight">CraftMyStrength</span>, we're driving a seismic shift by embedding cutting-edge science directly into footwear
            </p>
          </div>
        </div>
      </section>

      {/* Company Story */}
      <section className="company-story">
        <div className="container">
          <div className="story-content">
            <div className="story-text">
              <h2>Our Story</h2>
              <p>
                Over the past 50 years, the $500B footwear industry has focused on comfort, cushioning, 
                and aesthetics—yet it has missed an essential evolution: becoming truly smart. Unlike 
                industries transformed by innovation—like Uber with Transportation, we're pioneering 
                the next revolution in footwear.
              </p>
              <p>
                At Craft My Strength, we're driving a seismic shift by embedding cutting-edge science 
                directly into footwear. Our bio-responsive shoes actively enhance performance, strength, 
                recovery, and overall well-being.
              </p>
              <div className="story-stats">
                <div className="stat">
                  <h3>50%</h3>
                  <p>of Americans experience foot pain</p>
                </div>
                <div className="stat">
                  <h3>25%</h3>
                  <p>live physically inactive lives</p>
                </div>
                <div className="stat">
                  <h3>5M+</h3>
                  <p>deaths annually from physical inactivity</p>
                </div>
              </div>
            </div>
            <div className="story-visual">
              <div className="innovation-timeline">
                <div className="timeline-item">
                  <div className="timeline-dot"></div>
                  <div className="timeline-content">
                    <h4>Research & Development</h4>
                    <p>Extensive research into EMS and TENS technologies</p>
                  </div>
                </div>
                <div className="timeline-item">
                  <div className="timeline-dot"></div>
                  <div className="timeline-content">
                    <h4>Clinical Validation</h4>
                    <p>Validation by 5 orthopedic physicians and 15 physiotherapists</p>
                  </div>
                </div>
                <div className="timeline-item">
                  <div className="timeline-dot"></div>
                  <div className="timeline-content">
                    <h4>FDA Approval</h4>
                    <p>Successfully obtained FDA clearance (510K) for medical device</p>
                  </div>
                </div>
                <div className="timeline-item">
                  <div className="timeline-dot"></div>
                  <div className="timeline-content">
                    <h4>Market Launch</h4>
                    <p>Bringing I-Revive to global markets in 2025</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision - Updated without background circles */}
      <section className="mission-vision">
        <div className="container">
          <div className="section-header">
            <h2>Our Foundation</h2>
            <p>The principles that drive our innovation and guide our journey</p>
          </div>
          
          <div className="mv-modern-grid">
            {/* Mission Card */}
            <div className="mv-modern-card mission-card">
              <div className="card-inner">
                <div className="card-front">
                  <div className="card-icon-modern">
                    <div className="modern-simple-icon">
                      <div className="simple-circle"></div>
                      <div className="circle-inner"></div>
                      <div className="circle-dot"></div>
                    </div>
                  </div>
                  <h3>Our Mission</h3>
                  <p className="card-subtitle">Transforming Lives</p>
                </div>
                <div className="card-back">
                  <div className="back-content">
                    <h4>Revolutionary Impact</h4>
                    <p>
                      To revolutionize foot health by creating the world's first truly smart footwear 
                      that actively enhances performance, accelerates recovery, and improves quality 
                      of life for millions worldwide.
                    </p>
                    <div className="mission-stats">
                      <div className="stat-item">
                        <span className="stat-number">100+</span>
                        <span className="stat-label">Foot Muscles</span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-number">24/7</span>
                        <span className="stat-label">Health Support</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Vision Card */}
            <div className="mv-modern-card vision-card">
              <div className="card-inner">
                <div className="card-front">
                  <div className="card-icon-modern">
                    <div className="modern-simple-icon">
                      <div className="simple-circle"></div>
                      <div className="circle-inner"></div>
                      <div className="circle-dot"></div>
                    </div>
                  </div>
                  <h3>Our Vision</h3>
                  <p className="card-subtitle">Future Forward</p>
                </div>
                <div className="card-back">
                  <div className="back-content">
                    <h4>Tomorrow's Reality</h4>
                    <p>
                      A world where every step taken promotes health, where footwear transcends passive 
                      comfort to become an active partner in human wellness and mobility enhancement.
                    </p>
                    <div className="vision-goals">
                      <div className="goal-item">
                        <div className="goal-dot"></div>
                        <span>Global Health Impact</span>
                      </div>
                      <div className="goal-item">
                        <div className="goal-dot"></div>
                        <span>Smart Wellness Integration</span>
                      </div>
                      <div className="goal-item">
                        <div className="goal-dot"></div>
                        <span>Accessible Technology</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Values Card */}
            <div className="mv-modern-card values-card">
              <div className="card-inner">
                <div className="card-front">
                  <div className="card-icon-modern">
                    <div className="modern-simple-icon">
                      <div className="simple-circle"></div>
                      <div className="circle-inner"></div>
                      <div className="circle-dot"></div>
                    </div>
                  </div>
                  <h3>Our Values</h3>
                  <p className="card-subtitle">Core Principles</p>
                </div>
                <div className="card-back">
                  <div className="back-content">
                    <h4>What Drives Us</h4>
                    <div className="values-list">
                      <div className="value-item">
                        <div className="value-icon-small science"></div>
                        <span>Innovation through Science</span>
                      </div>
                      <div className="value-item">
                        <div className="value-icon-small patient"></div>
                        <span>Patient-Centered Design</span>
                      </div>
                      <div className="value-item">
                        <div className="value-icon-small excellence"></div>
                        <span>Clinical Excellence</span>
                      </div>
                      <div className="value-item">
                        <div className="value-icon-small wellness"></div>
                        <span>Accessible Wellness</span>
                      </div>
                      <div className="value-item">
                        <div className="value-icon-small sustainability"></div>
                        <span>Sustainable Technology</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="team-section">
        <div className="container">
          <div className="section-header">
            <h2>Meet Our Leadership Team</h2>
            <p>Experienced professionals driving innovation in health technology</p>
          </div>
        </div>
        
        <div className="team-container">
          <div className="team-grid">
            <div className="team-member">
              <div className="member-photo">
                <img src="/images/vivek.png" alt="Vivek Midha" onError={(e) => {e.target.style.opacity = '0'}} />
                <div className="photo-placeholder vivek">VM</div>
                <div className="photo-overlay"></div>
                <div className="member-role-badge">Founder & CEO</div>
              </div>
              <div className="member-info">
                <h3 className="member-name">Vivek Midha</h3>
                <h4 className="member-title">Founder & CEO</h4>
                <div className="member-details">
                  <p>30+ years of experience in Apparel & Wearable Technology</p>
                  <p>Executive MBA from Kellogg School of Management - HKUST</p>
                  <p>Pioneer in smart textile integration and IoT wearables</p>
                </div>
                <div className="member-achievements">
                  <span>Wearable Tech Expert</span>
                  <span>Business Strategy</span>
                  <span>Global Markets</span>
                </div>
              </div>
            </div>

            <div className="team-member">
              <div className="member-photo">
                <img src="/images/venkat.png" alt="Venkat Paditham" onError={(e) => {e.target.style.opacity = '0'}} />
                <div className="photo-placeholder venkat">VP</div>
                <div className="photo-overlay"></div>
                <div className="member-role-badge">Co-Founder</div>
              </div>
              <div className="member-info">
                <h3 className="member-name">Venkat Paditham</h3>
                <h4 className="member-title">Co-Founder & VP Strategy</h4>
                <div className="member-details">
                  <p>25+ years in IT Management and Technology Consulting</p>
                  <p>Chancellor Award recipient - VIT University</p>
                  <p>Executive MBA from Kellogg School of Management - HKUST</p>
                </div>
                <div className="member-achievements">
                  <span>Technology Strategy</span>
                  <span>Digital Innovation</span>
                  <span>Operations Excellence</span>

                </div>
              </div>
            </div>

            <div className="team-member">
              <div className="member-photo">
                <img src="/images/jatin.png" alt="Dr. Jatin Maheshwari" onError={(e) => {e.target.style.opacity = '0'}} />
                <div className="photo-placeholder jatin">JM</div>
                <div className="photo-overlay"></div>
                <div className="member-role-badge">Director</div>
              </div>
              <div className="member-info">
                <h3 className="member-name">Dr. Jatin Maheshwari</h3>
                <h4 className="member-title">Director of Exercise & Fitness</h4>
                <div className="member-details">
                  <p>Specialist in Olympic Sports Team Training and Biomechanics</p>
                  <p>Former Trainer for Indian National Sports Teams</p>
                  <p>Expert in athletic recovery and performance optimization</p>
                </div>
                <div className="member-achievements">
                  <span>Sports Medicine</span>
                  <span>Biomechanics</span>
                  <span>Athletic Recovery</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technology & Innovation */}
      <section className="tech-innovation">
        <div className="container">
          <div className="tech-content">
            <div className="tech-text">
              <h2>Leading the Innovation</h2>
              <p>
                Our breakthrough I-Revive technology represents the convergence of medical science, 
                wearable technology, and user-centered design.
              </p>
              
              <div className="innovation-points">
                <div className="innovation-item">
                  <div className="innovation-number">01</div>
                  <div>
                    <h4>World's First Integration</h4>
                    <p>EMS + TENS technologies in footwear form factor</p>
                  </div>
                </div>
                <div className="innovation-item">
                  <div className="innovation-number">02</div>
                  <div>
                    <h4>FDA-Cleared Medical Device</h4>
                    <p>Rigorous clinical testing and regulatory approval process</p>
                  </div>
                </div>
                <div className="innovation-item">
                  <div className="innovation-number">03</div>
                  <div>
                    <h4>Seamless Daily Use</h4>
                    <p>Lightweight, wireless, and ergonomic design for everyday wear</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="tech-visual">
              <div className="tech-showcase">
                <div className="tech-circle main">
                  <span>I-Revive</span>
                  <div className="tech-ring"></div>
                </div>
                <div className="tech-circle ems">
                  <span>EMS</span>
                </div>
                <div className="tech-circle tens">
                  <span>TENS</span>
                </div>
                <div className="connection-lines">
                  <div className="line line-1"></div>
                  <div className="line line-2"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Regulatory & Certifications */}
      <section className="certifications">
        <div className="container">
          <h2>Regulatory Approvals & Certifications</h2>
          <div className="cert-grid">
            <div className="cert-item">
              <div className="cert-badge fda">FDA</div>
              <h4>FDA Cleared (510K)</h4>
              <p>USFDA Approved by the U.S. Food & Drug Administration</p>
            </div>
            <div className="cert-item">
              <div className="cert-badge iec">IEC 60601</div>
              <h4>Medical Standards</h4>
              <p>Recognized under medical electrical equipment standards</p>
            </div>
            <div className="cert-item">
              <div className="cert-badge ce">CE</div>
              <h4>European Compliance</h4>
              <p>Ensuring compliance with European safety standards</p>
            </div>
            <div className="cert-item">
              <div className="cert-badge clinical">✓</div>
              <h4>Clinically Validated</h4>
              <p>Validated by Orthopedic Physicians and Physiotherapists</p>
            </div>
            <div className="cert-item">
              <div className="cert-badge patent">IP</div>
              <h4>Patent Pending</h4>
              <p>Patent application filed for proprietary technology</p>
            </div>
            <div className="cert-item">
              <div className="cert-badge trademark">™</div>
              <h4>Trademark Registered</h4>
              <p>To prevent misuse and ensure brand authenticity</p>
            </div>
          </div>
        </div>
      </section>

      {/* Simplified Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <h3>I-Revive</h3>
              <p className="tagline">Enhance Mobility, Accelerate Recovery</p>
              <p className="description">Revolutionary footwear technology that enhances mobility, accelerates recovery, and transforms foot health through advanced EMS and TENS integration.</p>
              <div className="footer-contact-info">
                <div className="footer-contact-item">
                  <span className="icon">✉</span>
                  <span>vivek@craftmystrength.com</span>
                </div>
                <div className="footer-contact-item">
                  <span className="icon">🌐</span>
                  <span>www.CraftMyStrength.com</span>
                </div>
                <div className="footer-contact-item">
                  <span className="icon">📍</span>
                  <span>Hong Kong & India</span>
                </div>
              </div>
            </div>
            
            <div className="footer-col">
              <h4>Product</h4>
              <a href="/about">About</a>
              <a href="/home">Product</a>
              <a href="/about">About Us</a>
            </div>
            
            <div className="footer-col">
              <h4>Support</h4>
              <a href="/contact">Contact Us</a>
            </div>
          </div>
          
          <div className="footer-bottom">
            <p>&copy; <span className="year">2025</span> CraftMyStrength. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default About;